/**
 * The product showcase gallery slider.
 *
 * Deliberately small: one image per arrow press, dots that track what is actually on screen,
 * and first/last positions that are always reachable. The theme's own slideshow advances a
 * whole page at a time, which reads as a skipped image when two slides are visible.
 */
class ProductShowcaseGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');

    if (!this.track || this.slides.length < 2) return;

    this.previousButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    for (const dot of this.dots) {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot)));
    }

    this.target = 0;
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    // Whatever the scroll settles on — arrow, dot or swipe — is the position from then on.
    this.track.addEventListener('scrollend', this.#onScrollEnd, { passive: true });
    window.addEventListener('resize', this.#onScroll);

    this.#sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.track?.removeEventListener('scrollend', this.#onScrollEnd);
    window.removeEventListener('resize', this.#onScroll);
  }

  /**
   * Distance between two consecutive slides, gap included.
   * @returns {number}
   */
  get #step() {
    const [first, second] = this.slides;
    if (!(first instanceof HTMLElement)) return 0;
    return second instanceof HTMLElement ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }

  /** @returns {number} The furthest index the track can actually scroll to. */
  get #lastIndex() {
    const step = this.#step;
    if (!step || !this.track) return this.slides.length - 1;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    return Math.max(0, Math.round(maxScroll / step));
  }

  /** @returns {number} The index the track is scrolled to right now. */
  get scrolledIndex() {
    const step = this.#step;
    if (!step || !this.track) return 0;
    return Math.round(this.track.scrollLeft / step);
  }

  /**
   * The index the slider is heading for. Tracked separately from the scroll position: reading
   * the position mid-animation returns a value between two slides, and stepping from that
   * rounds back to where we started, which swallows every other arrow press.
   *
   * @returns {number}
   */
  get index() {
    return this.target ?? this.scrolledIndex;
  }

  /** @param {number} delta */
  step(delta) {
    this.goTo(this.index + delta);
  }

  /** @param {number} index */
  goTo(index) {
    const step = this.#step;
    if (!step || !this.track) return;

    const target = Math.min(Math.max(index, 0), this.#lastIndex);
    this.target = target;

    // Mandatory snapping swallows programmatic smooth scrolls in Chrome, so it is lifted for
    // the duration of the move. The destination is a snap position anyway, so restoring it
    // afterwards changes nothing on screen.
    this.programmatic = true;
    this.track.style.scrollSnapType = 'none';

    // The snap change has to land before the scroll is queued, otherwise the browser is still
    // snapping when the request arrives and drops it.
    requestAnimationFrame(() => {
      this.track?.scrollTo({ left: target * step, behavior: 'smooth' });
    });

    clearTimeout(this.snapTimer);
    this.snapTimer = setTimeout(() => {
      if (this.track) this.track.style.scrollSnapType = '';
    }, 700);

    this.#sync();
  }

  #onScrollEnd = () => {
    // Our own scroll ending must not rewrite the target — a second arrow press queued while
    // the first was still animating would be undone by it.
    if (this.programmatic) {
      this.programmatic = false;
      this.#sync();
      return;
    }

    this.target = this.scrolledIndex;
    this.#sync();
  };

  #onScroll = () => {
    cancelAnimationFrame(this.frame ?? 0);
    this.frame = requestAnimationFrame(() => {
      // A finished animation, or a swipe, is the shopper's position now.
      if (!this.programmatic) {
        this.target = this.scrolledIndex;
        this.#sync();
      }
    });
  };

  #sync() {
    const index = this.index;
    const lastIndex = this.#lastIndex;

    for (const [position, dot] of this.dots.entries()) {
      // With two slides in view the track runs out before the last slide can lead, so those
      // positions are unreachable — showing a dot for them would leave a dot that never lights.
      dot.hidden = position > lastIndex;
      dot.setAttribute('aria-selected', String(position === index));
    }

    if (this.previousButton instanceof HTMLButtonElement) this.previousButton.disabled = index <= 0;
    if (this.nextButton instanceof HTMLButtonElement) this.nextButton.disabled = index >= lastIndex;
  }
}

if (!customElements.get('product-showcase-gallery')) {
  customElements.define('product-showcase-gallery', ProductShowcaseGallery);
}
